import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Ticket, Search, Calendar, Mail, User, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPasses = () => {
  const { getAuthHeader } = useAdminAuth();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [selectedPass, setSelectedPass] = useState(null);

  useEffect(() => {
    fetchPasses();
  }, [pagination.page, filters.status]);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo })
      });
      
      const response = await axios.get(`${API}/api/admin/passes?${params}`, {
        headers: getAuthHeader()
      });
      
      setPasses(response.data.passes);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (error) {
      console.error('Failed to fetch passes:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchPasses();
  };

  const handleActivatePass = async (purchaseId) => {
    console.log('handleActivatePass called with:', purchaseId);
    if (!purchaseId) {
      console.log('No purchaseId provided');
      return;
    }
    
    setActivating(true);
    console.log('Starting activation request...');
    try {
      const response = await axios.post(`${API}/api/admin/passes/activate`, 
        { purchaseId },
        { headers: getAuthHeader() }
      );
      
      console.log('Activation response:', response.data);
      
      if (response.data.success) {
        if (response.data.alreadyActive) {
          toast.info('Karnet był już wcześniej aktywowany');
        } else {
          toast.success(`Karnet "${response.data.passName}" został aktywowany!`);
        }
        // Refresh passes list
        fetchPasses();
        // Update selected pass if open AND close modal
        if (selectedPass && selectedPass.purchase_id === purchaseId) {
          setSelectedPass({
            ...selectedPass,
            activationStatus: 'active',
            activatedAt: new Date().toISOString(),
            fitnetKlientKarnetId: response.data.fitnetKlientKarnetId || selectedPass.fitnetKlientKarnetId
          });
        }
      } else {
        toast.error('Aktywacja nie powiodła się');
      }
    } catch (error) {
      console.error('Failed to activate pass:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errorPl || 'Nie udało się aktywować karnetu';
      toast.error(errorMsg);
    } finally {
      setActivating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      paid: 'Opłacony',
      pending: 'Oczekuje',
      cancelled: 'Anulowany',
      failed: 'Nieudany'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${styles[status] || 'bg-gray-700 text-gray-400'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Zakupione Karnety</h1>
          <p className="text-gray-400">Przeglądaj historię zakupów karnetów</p>
        </div>
        <div className="text-gray-400 text-sm">
          Łącznie: {pagination.total} karnetów
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Szukaj (UID, email, nazwa)"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="w-40">
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="paid">Opłacone</SelectItem>
                  <SelectItem value="pending">Oczekujące</SelectItem>
                  <SelectItem value="cancelled">Anulowane</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Od daty"
              />
            </div>
            <div className="w-40">
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Do daty"
              />
            </div>
            <Button onClick={handleSearch} className="bg-purple-600 hover:bg-purple-700">
              <Search className="w-4 h-4 mr-2" />
              Szukaj
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Passes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
        </div>
      ) : (
        <div className="space-y-3 mt-8">
          {passes.map((pass) => (
            <Card 
              key={pass.purchase_id} 
              className="mb-4 bg-white/5 border-white/10 hover:border-purple-500/50 transition-colors cursor-pointer"
              onClick={() => setSelectedPass(pass)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Ticket className="w-6 h-6 text-purple-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-5 gap-2">
                    <div>
                      <p className="text-white font-medium truncate">{pass.passName}</p>
                      <p className="text-gray-500 text-xs font-mono">{pass.pass_code || pass.passUid || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Kupujący</p>
                      <p className="text-white text-sm flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {pass.buyerName || 'Nieznany'}
                      </p>
                      <p className="text-gray-400 text-xs truncate">{pass.buyerEmail || pass.buyer_email || '-'}</p>
                      {pass.buyerPhone && (
                        <p className="text-gray-400 text-xs">{pass.buyerPhone}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Dla kogo</p>
                      <p className="text-white text-sm">
                        {pass.forUserName || pass.buyerName || '-'}
                      </p>
                      {pass.forUserName && pass.forUserName !== pass.buyerName && (
                        <span className="text-xs text-purple-400">(subkonto)</span>
                      )}
                      {pass.forUserPhone && pass.forUserPhone !== pass.buyerPhone && (
                        <p className="text-gray-400 text-xs">{pass.forUserPhone}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Cena zakupu</p>
                      <p className="text-white font-medium">{((pass.passPrice || pass.price) || 0).toFixed(2)} zł</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(pass.createdAt).toLocaleDateString('pl-PL')}
                      </p>
                    </div>
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {getStatusBadge(pass.paymentStatus)}
                      {pass.isGift && (
                        <span className="px-2 py-0.5 text-xs rounded bg-pink-500/20 text-pink-400">
                          Prezent
                        </span>
                      )}
                      {/* Activation status badge */}
                      {pass.paymentStatus === 'paid' && (
                        pass.activationStatus === 'active' || pass.activateImmediately ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-emerald-400" title={pass.activatedAt ? `Aktywowany: ${new Date(pass.activatedAt).toLocaleString('pl-PL')}` : 'Natychmiastowa aktywacja'}>
                            ✓ Aktywowany
                          </span>
                        ) : pass.activationStatus === 'pending_activation' ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-amber-500/20 text-amber-400">
                            Oczekuje na aktywację
                          </span>
                        ) : null
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {passes.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <Ticket className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">Brak karnetów spełniających kryteria</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-gray-400">
            Strona {pagination.page} z {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
            disabled={pagination.page === pagination.pages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Pass Details Modal */}
      {selectedPass && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPass(null)}>
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-400" />
                Szczegóły karnetu
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-gray-400 text-sm">Kod karnetu</p>
                <p className="text-2xl font-mono text-purple-400 font-bold">{selectedPass.pass_code || selectedPass.passUid || '-'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Nazwa karnetu</p>
                  <p className="text-white">{selectedPass.passName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Cena</p>
                  <p className="text-white">{(selectedPass.passPrice || selectedPass.price || 0).toFixed(2)} zł</p>
                </div>
                <div>
                  <p className="text-gray-500">Kupujący</p>
                  <p className="text-white">{selectedPass.buyerName || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Telefon kupującego</p>
                  <p className="text-white">{selectedPass.buyerPhone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Dla kogo</p>
                  <p className="text-white">{selectedPass.forUserName || selectedPass.buyerName || '-'}</p>
                  {selectedPass.forUserName && selectedPass.forUserName !== selectedPass.buyerName && (
                    <span className="text-xs text-purple-400">(subkonto)</span>
                  )}
                </div>
                <div>
                  <p className="text-gray-500">Telefon odbiorcy</p>
                  <p className="text-white">{selectedPass.forUserPhone || selectedPass.buyerPhone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email kupującego</p>
                  <p className="text-white truncate">{selectedPass.buyerEmail || selectedPass.buyer_email || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Data zakupu</p>
                  <p className="text-white">{new Date(selectedPass.createdAt).toLocaleString('pl-PL')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status płatności</p>
                  {getStatusBadge(selectedPass.paymentStatus)}
                </div>
                <div>
                  <p className="text-gray-500">Status aktywacji</p>
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    selectedPass.activationStatus === 'active' || selectedPass.activateImmediately ? 'bg-emerald-500/20 text-emerald-400' :
                    selectedPass.activationStatus === 'pending_activation' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {selectedPass.activationStatus === 'active' || selectedPass.activateImmediately ? 'Aktywowany' :
                     selectedPass.activationStatus === 'pending_activation' ? 'Oczekuje' : 
                     selectedPass.fitnetStatus || '-'}
                  </span>
                </div>
                {(selectedPass.activatedAt || (selectedPass.activateImmediately && selectedPass.paidAt)) && (
                  <div>
                    <p className="text-gray-500">Data aktywacji</p>
                    <p className="text-white">
                      {new Date(selectedPass.activatedAt || selectedPass.paidAt).toLocaleString('pl-PL')}
                    </p>
                  </div>
                )}
                {selectedPass.fitnetKlientKarnetId && (
                  <div>
                    <p className="text-gray-500">FitNet ID karnetu</p>
                    <p className="text-white">{selectedPass.fitnetKlientKarnetId}</p>
                  </div>
                )}
                {selectedPass.fitnetClientId && (
                  <div>
                    <p className="text-gray-500">FitNet ID klienta</p>
                    <p className="text-white">{selectedPass.fitnetClientId}</p>
                  </div>
                )}
                {selectedPass.isGift && (
                  <div>
                    <p className="text-gray-500">Prezent dla</p>
                    <p className="text-white">{selectedPass.giftEmail}</p>
                  </div>
                )}
              </div>

              {/* Activate button - only for pending passes that have fitnetClientId */}
              {selectedPass.paymentStatus === 'paid' && 
               selectedPass.activationStatus !== 'active' && 
               !selectedPass.activateImmediately &&
               selectedPass.fitnetClientId &&
               !selectedPass.isGift && (
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-400 font-medium">Karnet oczekuje na aktywację</p>
                      <p className="text-sm text-gray-400">Kliknij aby aktywować karnet dla użytkownika</p>
                    </div>
                    <Button 
                      onClick={() => handleActivatePass(selectedPass.purchase_id)}
                      disabled={activating}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {activating ? 'Aktywowanie...' : 'Aktywuj karnet'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Gift pass notice */}
              {selectedPass.isGift && !selectedPass.for_user_id && (
                <div className="p-4 bg-pink-500/10 rounded-lg border border-pink-500/30">
                  <p className="text-pink-400 text-sm">
                    To jest karnet-prezent. Nie można go aktywować z panelu admina - musi zostać odebrany przez obdarowaną osobę.
                  </p>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => setSelectedPass(null)}>
                  Zamknij
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPasses;
